import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { PrimeNGConfig } from "primeng/api";
import {
  DateRange,
  PERIOD_PRESETS,
  PeriodPreset,
  formatDisplayDate,
  matchingPreset,
  presetRange,
  rangeDiffInDays,
  rangeInDays,
  startOfDay
} from "src/app/utils/period-filter.util";

@Component({
  selector: "app-period-filter",
  templateUrl: "./period-filter.component.html",
  styleUrls: ["./period-filter.component.scss"]
})
export class PeriodFilterComponent implements OnInit {
  private static instances = 0;

  // Evita ids duplicados quando mais de um filtro é renderizado na mesma tela.
  readonly uid = `period-filter-${PeriodFilterComponent.instances++}`;
  readonly presets = PERIOD_PRESETS;

  // Instância única: o p-calendar recebe a data por binding e recriá-la a cada
  // ciclo de detecção de mudanças dispararia ngOnChanges sem necessidade.
  readonly today = startOfDay(new Date());

  dataInicial: Date | null = null;
  dataFinal: Date | null = null;

  @Input() set range(value: DateRange) {
    this.dataInicial = value?.dataInicial ?? null;
    this.dataFinal = value?.dataFinal ?? null;
  }

  @Input() total = 0;
  @Input() totalLabel = "notas emitidas";

  // Quando informado, avisa que a exportação em lote não aceita o período atual.
  @Input() maxExportRangeInDays?: number;

  @Output() rangeChange = new EventEmitter<DateRange>();

  constructor(private readonly primengConfig: PrimeNGConfig) {}

  ngOnInit(): void {
    this.primengConfig.setTranslation({
      dayNames: [
        "Domingo",
        "Segunda-feira",
        "Terça-feira",
        "Quarta-feira",
        "Quinta-feira",
        "Sexta-feira",
        "Sábado"
      ],
      dayNamesShort: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
      dayNamesMin: ["D", "S", "T", "Q", "Q", "S", "S"],
      monthNames: [
        "Janeiro",
        "Fevereiro",
        "Março",
        "Abril",
        "Maio",
        "Junho",
        "Julho",
        "Agosto",
        "Setembro",
        "Outubro",
        "Novembro",
        "Dezembro"
      ],
      monthNamesShort: [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez"
      ],
      today: "Hoje",
      clear: "Limpar",
      dateFormat: "dd/mm/yy",
      weekHeader: "Sem"
    });
  }

  get activePreset(): PeriodPreset | null {
    return matchingPreset(this.currentRange);
  }

  // Impede que a data final seja anterior à inicial já na seleção do calendário.
  get endDateMin(): Date | undefined {
    return this.dataInicial ?? undefined;
  }

  get hasPeriod(): boolean {
    return !!(this.dataInicial || this.dataFinal);
  }

  get days(): number {
    return rangeInDays(this.currentRange);
  }

  get exportRangeExceeded(): boolean {
    return (
      !!this.maxExportRangeInDays &&
      rangeDiffInDays(this.currentRange) > this.maxExportRangeInDays
    );
  }

  get periodLabel(): string {
    if (!this.dataInicial && !this.dataFinal) {
      return "Exibindo todos os períodos";
    }

    if (!this.dataInicial || !this.dataFinal) {
      return "Selecione as duas datas para aplicar o filtro";
    }

    const days = this.days;

    return `Exibindo ${formatDisplayDate(this.dataInicial)} até ${formatDisplayDate(this.dataFinal)} (${days} ${days === 1 ? "dia" : "dias"})`;
  }

  private get currentRange(): DateRange {
    return { dataInicial: this.dataInicial, dataFinal: this.dataFinal };
  }

  applyPreset(preset: PeriodPreset) {
    const range = presetRange(preset);
    this.dataInicial = range.dataInicial;
    this.dataFinal = range.dataFinal;
    this.emit();
  }

  clear() {
    this.dataInicial = null;
    this.dataFinal = null;
    this.emit();
  }

  onStartDateChange(date: Date | null) {
    this.dataInicial = date;

    // Período invertido: mantém as pontas coerentes sem descartar a seleção.
    if (
      this.dataInicial &&
      this.dataFinal &&
      this.dataInicial > this.dataFinal
    ) {
      this.dataFinal = new Date(this.dataInicial);
    }

    this.emit();
  }

  onEndDateChange(date: Date | null) {
    this.dataFinal = date;
    this.emit();
  }

  private emit() {
    this.rangeChange.emit(this.currentRange);
  }
}
